import type React from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input } from '@m/alchemy-ui/Input';
import { AlertInfo, FieldsetGroup, Info, Item, Label, SrOnlyLegend, Tag } from './Onboard.styled';

interface SkillsProps {
  skills?: string[];
  tagColor: string;
}

const Skills = ({ skills: initialSkills = [], tagColor }: SkillsProps) => {
  const intl = useIntl();
  const [skills, setSkills] = useState<string[]>(initialSkills);
  const [inputValue, setInputValue] = useState('');
  const [showNoTagsMessage, setShowNoTagsMessage] = useState(false);

  useEffect(() => {
    if (initialSkills && initialSkills.join(',') !== skills.join(',')) {
      setSkills(initialSkills);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSkills]);

  useEffect(() => {
    setShowNoTagsMessage(skills.length === 0);
  }, [skills]);

  const handleRemoveSkill = (skillToRemove: string) => {
    const newSkills = skills.filter((skill) => skill !== skillToRemove);
    setSkills(newSkills);
  };

  const processInput = () => {
    const parts = Array.from(
      new Set(
        inputValue
          .split(',')
          .map((skill) => skill.trim())
          .filter((skill) => skill.length > 0)
      )
    );

    const newSkills = parts.filter(
      (newSkill) => !skills.some((existingSkill) => existingSkill.toLowerCase() === newSkill.toLowerCase())
    );

    if (newSkills.length > 0) {
      setSkills((prev) => [...prev, ...newSkills]);
    }

    setInputValue('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processInput();
    }
  };

  const handleInputBlur = () => {
    processInput();
  };

  const skillsAndExpertise = intl.formatMessage({
    id: 'skillsAndExpertise',
    defaultMessage: 'Skills and Expertise'
  });

  return (
    <FieldsetGroup>
      <SrOnlyLegend>{skillsAndExpertise}</SrOnlyLegend>
      <Item>
        <Label htmlFor="newPostTagOnboard">{skillsAndExpertise}</Label>

        <div id="postFormTagAreaOnboard" className="mdl-form__tags">
          {skills.map((skill) => (
            <div key={skill} className="mdl-form__tags-item">
              <Tag color={tagColor}>{skill}</Tag>
              <button type="button" onClick={() => handleRemoveSkill(skill)} className="mdl-icon tagDeleteButton">
                <svg aria-hidden="true">
                  <use href="#cancel" />
                </svg>
              </button>
            </div>
          ))}

          <Input
            id="newPostTagOnboard"
            name="newPostTag"
            autoComplete="off"
            data-is-profile="false"
            value={inputValue}
            onChange={(e) => handleInputChange(e as React.ChangeEvent<HTMLInputElement>)}
            onKeyDown={(e) => handleInputKeyDown(e as React.KeyboardEvent<HTMLInputElement>)}
            onBlur={handleInputBlur}
            aria-describedby="skillsInstructions noTagsMessageOnboard"
            isFullWidth
            placeholder={intl.formatMessage({
              id: 'skillsInputPlaceholder',
              defaultMessage: 'Type skills here'
            })}
          />
        </div>

        {showNoTagsMessage && (
          <AlertInfo id="noTagsMessageOnboard">
            {intl.formatMessage({ id: 'noTagsSelected', defaultMessage: 'No tags selected' })}
          </AlertInfo>
        )}

        <Info id="skillsInstructions">
          {intl.formatMessage({
            id: 'skillsAndExpertiseInstructions',
            defaultMessage:
              "Please enter a list of your skills and areas of expertise, separated by commas eg 'Project management, Data analytics, Digital marketing'"
          })}
        </Info>

        <Input type="hidden" name="user_tags" value={skills.join(',')} />
      </Item>
    </FieldsetGroup>
  );
};

export default Skills;
